import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search, Download, Mail, Users, RefreshCw, Trash2,
  Ticket as TicketIcon, DollarSign, MessageCircle, Send,
  Printer, CheckCircle2, XCircle, AlertTriangle, Edit, X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
};

const SOURCE_LABELS = {
  stripe: "Stripe", manual: "Manual", interac_e_transfer: "Interac", cash: "Cash",
};

const AdminMarketCampTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("paid");
  const [selected, setSelected] = useState([]);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  const loadAll = async () => {
    try {
      const [t, w] = await Promise.all([
        axios.get(`${API}/admin/market-camp-tickets`),
        axios.get(`${API}/admin/whatsapp-config-status`).catch(() => ({ data: { twilio_configured: false } })),
      ]);
      return { tickets: t.data || [], waOn: !!w.data?.twilio_configured };
    } catch {
      toast.error("Failed to load tickets");
      return null;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const d = await loadAll();
    if (d) { setTickets(d.tickets); setWhatsappConfigured(d.waOn); }
    setLoading(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const d = await loadAll();
      if (active && d) { setTickets(d.tickets); setWhatsappConfigured(d.waOn); setLoading(false); }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter: by source + status + search
  const filtered = tickets.filter(t => {
    if (statusFilter !== "all" && t.payment_status !== statusFilter) return false;
    if (sourceFilter !== "all" && t.source !== sourceFilter) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      t.ticket_code?.toLowerCase().includes(q) ||
      t.child_name?.toLowerCase().includes(q) ||
      t.parent_full_name?.toLowerCase().includes(q) ||
      t.parent_email?.toLowerCase().includes(q) ||
      t.parent_phone?.includes(q) ||
      t.registration_code?.toLowerCase().includes(q)
    );
  });

  // Stats — mirror Suds Guests Legacy: Confirmed Bookings / Total Tickets / Total Revenue / Selected
  const paidTickets = filtered.filter(t => t.payment_status === "paid");
  const uniqueRegs = new Set();
  let revenue = 0;
  paidTickets.forEach(t => {
    if (t.registration_id && !uniqueRegs.has(t.registration_id)) {
      uniqueRegs.add(t.registration_id);
      revenue += t.registration_total || 0;
    }
  });
  const confirmedBookings = uniqueRegs.size;
  const totalTickets = paidTickets.length;

  // Source dropdown options (only sources actually present)
  const uniqueSources = Array.from(new Set(tickets.map(t => t.source).filter(Boolean)));

  const toggleSelect = (code) => setSelected(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  const selectAllVisible = () => setSelected(filtered.map(t => t.ticket_code));
  const clearSelection = () => setSelected([]);

  const handleExportCSV = () => {
    const csv = [
      ["Guest", "Email", "Phone", "Ticket #", "Booking Code", "Event", "Group", "Qty", "Amount", "Source", "Date"].join(","),
      ...filtered.map(t => [
        `"${t.parent_full_name || ""}"`, `"${t.parent_email || ""}"`, `"${t.parent_phone || ""}"`,
        t.ticket_code || "",
        t.registration_code || "",
        `"${t.child_name || ""} (age ${t.child_age || ""})"`,
        t.child_age_group || "",
        1,
        (t.ticket_share || 0).toFixed(2),
        SOURCE_LABELS[t.source] || t.source || "",
        t.created_at?.split("T")[0] || "",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `market-camp-tickets_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link); link.click(); link.remove();
    toast.success("Ticket list exported");
  };

  const emailSelected = async () => {
    if (selected.length === 0) return;
    const regIds = Array.from(new Set(tickets.filter(t => selected.includes(t.ticket_code)).map(t => t.registration_id)));
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/bulk-resend-email`, { registration_ids: regIds });
      toast.success(`Email queued for ${res.data.queued} parent(s)`);
      setShowEmailModal(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk email failed"); }
  };

  const whatsappSelected = async () => {
    if (selected.length === 0) return;
    const regIds = Array.from(new Set(tickets.filter(t => selected.includes(t.ticket_code)).map(t => t.registration_id)));
    try {
      const res = await axios.post(`${API}/admin/market-camp-registrations/bulk-resend-whatsapp`, { registration_ids: regIds });
      toast.success(`WhatsApp queued for ${res.data.queued} parent(s)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk WhatsApp failed"); }
  };

  const toggleCheckIn = async (ticket_code, current) => {
    try {
      await axios.patch(`${API}/admin/market-camp-tickets/${ticket_code}/check-in`, { checked_in: !current });
      toast.success(`Ticket ${ticket_code} ${!current ? "checked in" : "un-checked"}`);
      fetchData();
    } catch { toast.error("Failed to update check-in"); }
  };

  const resendEmail = async (registration_id) => {
    try {
      await axios.post(`${API}/admin/market-camp-registrations/${registration_id}/resend-email`);
      toast.success("Email re-sent to parent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to resend"); }
  };

  const resendWhatsApp = async (registration_id) => {
    if (whatsappConfigured) {
      try {
        await axios.post(`${API}/admin/market-camp-registrations/${registration_id}/resend-whatsapp`);
        toast.success("WhatsApp sent to parent");
        fetchData();
        return;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "WhatsApp failed — opening manual chat");
      }
    }
    try {
      const r = await axios.get(`${API}/admin/market-camp-registrations/${registration_id}/whatsapp-link`);
      if (!r.data.parent_whatsapp_url) { toast.error("No parent phone on file."); return; }
      window.open(r.data.parent_whatsapp_url, "_blank");
    } catch { toast.error("Failed to open WhatsApp"); }
  };

  const printTicket = (t) => {
    const win = window.open("", "_blank");
    if (!win) { toast.error("Pop-up blocked. Allow pop-ups to print tickets."); return; }
    const qrSrc = document.getElementById(`__qr-${t.ticket_code}`);
    let qrSvgMarkup = "";
    if (qrSrc) {
      const inner = qrSrc.querySelector("svg");
      if (inner) {
        const clone = inner.cloneNode(true);
        clone.setAttribute("width", "200"); clone.setAttribute("height", "200");
        qrSvgMarkup = clone.outerHTML;
      }
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Ticket · ${t.ticket_code}</title>
      <style>@page{size:100mm 150mm;margin:6mm;}body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1a1a1a;margin:0;padding:24px;text-align:center;}
      .card{border:3px solid #5B2A86;border-radius:16px;padding:18px;}
      h1{color:#3F1A66;font-size:18px;margin:0 0 4px;}
      .sub{color:#F2762E;font-size:11px;text-transform:uppercase;letter-spacing:2px;font-weight:800;margin-bottom:12px;}
      .code{font-family:monospace;font-size:24px;font-weight:900;letter-spacing:4px;color:#3F1A66;margin:12px 0 6px;}
      .name{font-weight:bold;font-size:16px;margin-bottom:2px;}
      .meta{color:#555;font-size:12px;margin-bottom:12px;}
      svg{display:block;margin:0 auto;}
      .foot{color:#777;font-size:10px;margin-top:14px;}</style></head><body>
      <div class="card"><h1>Mini Makers Market Camp</h1><p class="sub">Official Ticket</p>
      ${qrSvgMarkup}<p class="code">${t.ticket_code}</p>
      <p class="name">${t.child_name}</p>
      <p class="meta">Age ${t.child_age} · Group ${t.child_age_group || "—"}</p>
      <p class="meta">Parent: ${t.parent_full_name}</p>
      <p class="meta">Reg #${t.registration_code}</p>
      <p class="foot">Bring this ticket on Day 1 · Mix & Mingle Events</p>
      </div><script>window.onload=()=>{setTimeout(()=>window.print(),250);};</script></body></html>`);
    win.document.close();
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-market-camp-tickets-page">
        {/* Header — exact Suds Guests Legacy style */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Guests (Legacy)
            </h1>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Unified per-child ticket view across Stripe + manual Market Camp registrations
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => selected.length > 0 && setShowEmailModal(true)}
              disabled={selected.length === 0}
              className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37]/10 disabled:opacity-50"
              data-testid="email-attendees-btn"
            >
              <Send className="w-4 h-4" /> Email ({selected.length})
            </button>
            <button
              onClick={() => selected.length > 0 ? whatsappSelected() : toast.error("Select tickets first")}
              disabled={selected.length === 0}
              className="flex items-center gap-2 px-4 py-2 border border-green-500 text-green-400 rounded hover:bg-green-500/10 disabled:opacity-50"
              data-testid="whatsapp-attendees-btn"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp ({selected.length})
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] rounded hover:text-[#D4AF37]"
              data-testid="export-guests-btn"
            >
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>

        {!whatsappConfigured && (
          <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-4 py-2.5 text-xs text-yellow-300">
            ⚠️ Automated WhatsApp not configured. WhatsApp buttons open a pre-filled chat for manual sending. Add <code>TWILIO_*</code> env vars to enable server-side automation.
          </div>
        )}

        {/* Stats — exact Suds Guests Legacy layout (4 cards) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <Users className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]" data-testid="stat-confirmed-bookings">{confirmedBookings}</p>
            <p className="text-[#A1A1AA] text-sm">Confirmed Bookings</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <TicketIcon className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]" data-testid="stat-total-tickets">{totalTickets}</p>
            <p className="text-[#A1A1AA] text-sm">Total Tickets</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <DollarSign className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]" data-testid="stat-revenue">C${revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[#A1A1AA] text-sm">Total Revenue</p>
          </div>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.15)]">
            <Mail className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-2xl font-bold text-[#F5F5F0]" data-testid="stat-selected">{selected.length}</p>
            <p className="text-[#A1A1AA] text-sm">Selected</p>
          </div>
        </div>

        {/* Filters — exact Suds layout: search + source dropdown */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search by name, email, or booking code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 rounded focus:border-[#D4AF37] focus:outline-none"
              data-testid="ticket-search"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
            data-testid="source-filter"
          >
            <option value="all">All Sources</option>
            {uniqueSources.map(s => <option key={s} value={s}>{SOURCE_LABELS[s] || s}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
            data-testid="status-filter"
          >
            <option value="paid">Paid only</option>
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {/* Select All */}
        <div className="flex items-center gap-4">
          <button onClick={selectAllVisible} className="text-sm text-[#D4AF37] hover:underline" data-testid="select-all-visible">
            Select All Visible ({filtered.length})
          </button>
          {selected.length > 0 && (
            <button onClick={clearSelection} className="text-sm text-[#A1A1AA] hover:text-[#F5F5F0]">
              Clear Selection
            </button>
          )}
        </div>

        {/* Table — exact Suds Guests Legacy columns: Guest, Ticket #, Booking Code, Event, Ticket, Qty, Amount, Source, Date, Actions */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-[#0F0F0F] rounded border border-[rgba(212,175,55,0.15)]">
            <Users className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No tickets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tickets-table">
              <thead>
                <tr className="border-b border-[rgba(212,175,55,0.15)]">
                  <th className="p-3 text-left">
                    <input
                      type="checkbox"
                      checked={selected.length === filtered.length && filtered.length > 0}
                      onChange={() => selected.length === filtered.length ? clearSelection() : selectAllVisible()}
                      className="rounded border-[#D4AF37]"
                    />
                  </th>
                  <th className="p-3 text-left">QR</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Guest</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Ticket #</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Booking Code</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Event</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Ticket</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Qty</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Amount</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Source</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Date</th>
                  <th className="p-3 text-left text-[#A1A1AA] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const hasAllergy = t.allergies && t.allergies.trim().toLowerCase() !== "none";
                  const isSelected = selected.includes(t.ticket_code);
                  return (
                    <motion.tr
                      key={t.ticket_code}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className={`border-b border-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.05)] ${isSelected ? "bg-[rgba(212,175,55,0.04)]" : ""}`}
                      data-testid={`ticket-row-${t.ticket_code}`}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(t.ticket_code)}
                          className="rounded border-[#D4AF37]"
                          data-testid={`select-ticket-${t.ticket_code}`}
                        />
                      </td>
                      <td className="p-3">
                        <div id={`__qr-${t.ticket_code}`} data-testid={`ticket-qr-${t.ticket_code}`} className="bg-white p-1 rounded inline-block">
                          <QRCodeSVG value={t.ticket_code} size={48} level="M" />
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-[#F5F5F0] font-medium">{t.parent_full_name}</p>
                        <p className="text-[#A1A1AA] text-sm">{t.parent_email}</p>
                        {t.parent_phone && <p className="text-[#a1a1aa] text-xs">{t.parent_phone}</p>}
                      </td>
                      <td className="p-3">
                        <span className="text-[#22C55E] font-mono text-sm font-bold" data-testid={`ticket-code-${t.ticket_code}`}>{t.ticket_code}</span>
                        {t.checked_in && <p className="text-green-400 text-[10px] mt-0.5">✓ Checked in</p>}
                      </td>
                      <td className="p-3">
                        <span className="text-[#D4AF37] font-mono text-sm">{t.registration_code}</span>
                      </td>
                      <td className="p-3 text-[#F5F5F0]">
                        Mini Makers Market Camp
                        {hasAllergy && (
                          <p className="text-red-400 text-[10px] mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t.allergies}</p>
                        )}
                      </td>
                      <td className="p-3 text-[#A1A1AA]">
                        {t.child_name} (age {t.child_age})
                        <p className="text-[10px] text-[#52525B]">Group {t.child_age_group || "—"}</p>
                      </td>
                      <td className="p-3 text-[#A1A1AA]">1</td>
                      <td className="p-3 text-[#D4AF37]">C${(t.ticket_share || 0).toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          t.source === "stripe" ? "bg-blue-900/30 text-blue-400"
                          : t.source === "interac_e_transfer" ? "bg-emerald-900/30 text-emerald-400"
                          : "bg-purple-900/30 text-purple-400"
                        }`} data-testid={`ticket-source-${t.ticket_code}`}>
                          {SOURCE_LABELS[t.source] || t.source}
                        </span>
                      </td>
                      <td className="p-3 text-[#A1A1AA] text-sm">{formatDate(t.created_at)}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button
                            onClick={() => toggleCheckIn(t.ticket_code, t.checked_in)}
                            disabled={t.payment_status !== "paid"}
                            className={`p-2 rounded transition-colors ${t.checked_in ? "bg-green-500 text-white" : "bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                            title={t.checked_in ? "Un-check-in" : "Check in"}
                            data-testid={`checkin-${t.ticket_code}`}
                          >
                            {t.checked_in ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                          </button>
                          <button onClick={() => printTicket(t)} className="p-2 bg-purple-900/30 text-purple-400 hover:bg-purple-900/50 rounded transition-colors" title="Print ticket" data-testid={`print-ticket-${t.ticket_code}`}>
                            <Printer className="w-4 h-4" />
                          </button>
                          <button onClick={() => resendEmail(t.registration_id)} className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded transition-colors" title="Resend email" data-testid={`resend-email-${t.ticket_code}`}>
                            <Mail className="w-4 h-4" />
                          </button>
                          <button onClick={() => resendWhatsApp(t.registration_id)} className="p-2 bg-green-900/30 text-green-400 hover:bg-green-900/50 rounded transition-colors" title={whatsappConfigured ? "Send WhatsApp (automated)" : "Open WhatsApp (manual)"} data-testid={`resend-whatsapp-${t.ticket_code}`}>
                            <MessageCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email confirm modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#F5F5F0] mb-2">Email {selected.length} parent(s)?</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">Re-sends the registration confirmation email with ticket codes.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#F5F5F0]">Cancel</button>
              <button onClick={emailSelected} className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold">Send</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMarketCampTickets;
